import {
  type ProfileTemplate,
  ProfileTemplatesApi,
} from '@up2stay/up2promo-api-client';
import { Button, Label, Skeleton } from '@up2stay/up2promo-ui';
import { ExternalLink } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import {
  SettingsGroupRow,
  SettingsGroupRows,
} from '@/core/components/settings/settings-group';
import { SettingsSection } from '@/core/components/settings/settings-section';
import { TeamTemplateCombobox } from '@/core/components/workspace/teams/team-template-combobox';
import { useNavigateShell } from '@/core/hooks/use-navigate-shell';
import { fetchAllProfileTemplatesPages } from '@/core/lib/profile-templates-fetch-all';
import { shellPaths } from '@/core/router/app-paths';

const TEMPLATE_TRIGGER_CLASS =
  'flex h-10 w-full min-w-0 cursor-pointer items-center rounded-[var(--r-input)] border border-input bg-card px-3 py-2 text-[13.5px] font-normal leading-5 text-foreground shadow-xs dark:bg-input/30';

function applyTeamAssignment(
  templates: ProfileTemplate[],
  teamId: string,
  assignedTemplateId: string | null,
): ProfileTemplate[] {
  return templates.map((t) => {
    const hasTeam = t.teamIds?.includes(teamId) ?? false;
    const shouldHave = t.id === assignedTemplateId;
    if (hasTeam === shouldHave) return t;
    const nextTeamIds = shouldHave
      ? [...new Set([...(t.teamIds ?? []), teamId])]
      : (t.teamIds ?? []).filter((id) => id !== teamId);
    return { ...t, teamIds: nextTeamIds };
  });
}

interface TeamProfileTemplatesSectionProps {
  workspaceId: string;
  teamId: string;
  canManage: boolean;
  locale: string;
  dict: {
    title: string;
    description: string;
    templateLabel: string;
    templatePlaceholder: string;
    templateSearch: string;
    templateNone: string;
    templateEmpty: string;
    none: string;
    openTemplate: string;
    error: string;
  };
}

export function TeamProfileTemplatesSection({
  workspaceId,
  teamId,
  canManage,
  locale,
  dict,
}: TeamProfileTemplatesSectionProps) {
  const { navigateShell } = useNavigateShell();
  const [allTemplates, setAllTemplates] = useState<ProfileTemplate[]>([]);
  const [assignedTemplateId, setAssignedTemplateId] = useState<string | null>(
    null,
  );
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await fetchAllProfileTemplatesPages(workspaceId);
      setAllTemplates(list);
      const assigned = list.find((t) => t.teamIds?.includes(teamId));
      setAssignedTemplateId(assigned?.id ?? null);
    } catch (err) {
      console.error(err);
      toast.error(dict.error);
    } finally {
      setLoading(false);
    }
  }, [workspaceId, teamId, dict.error]);

  useEffect(() => {
    void (async () => {
      await load();
    })();
  }, [load]);

  const assignedTemplate = useMemo(
    () => allTemplates.find((t) => t.id === assignedTemplateId) ?? null,
    [allTemplates, assignedTemplateId],
  );

  const triggerLabel = assignedTemplate?.name ?? dict.templateNone;

  const persistAssignment = useCallback(
    (nextTemplateId: string | null) => {
      if (!canManage) return;
      if (nextTemplateId === assignedTemplateId) return;

      const previousTemplateId = assignedTemplateId;
      const previousTemplates = allTemplates;

      setAssignedTemplateId(nextTemplateId);
      setAllTemplates((prev) =>
        applyTeamAssignment(prev, teamId, nextTemplateId),
      );

      const updates = previousTemplates.filter((t) => {
        const was = t.teamIds?.includes(teamId) ?? false;
        const shouldBe = t.id === nextTemplateId;
        return was !== shouldBe;
      });

      void (async () => {
        try {
          await Promise.all(
            updates.map((t) => {
              const shouldBe = t.id === nextTemplateId;
              const nextTeamIds = shouldBe
                ? [...new Set([...(t.teamIds ?? []), teamId])]
                : (t.teamIds ?? []).filter((id) => id !== teamId);
              return ProfileTemplatesApi.update({
                path: { workspaceId, templateId: t.id },
                body: { teamIds: nextTeamIds },
              });
            }),
          );
        } catch (err) {
          console.error(err);
          setAssignedTemplateId(previousTemplateId);
          setAllTemplates(previousTemplates);
          toast.error(dict.error);
        }
      })();
    },
    [
      allTemplates,
      assignedTemplateId,
      canManage,
      dict.error,
      teamId,
      workspaceId,
    ],
  );

  if (!canManage && !assignedTemplateId) {
    return null;
  }

  return (
    <SettingsSection
      id="team-profile-template"
      title={dict.title}
      footer={dict.description}
      grouped
    >
      <SettingsGroupRows>
        <SettingsGroupRow>
          {loading ? (
            <Skeleton className="h-10 w-full" />
          ) : allTemplates.length === 0 ? (
            <p className="text-[13.5px] leading-relaxed text-muted-foreground">
              {dict.none}
            </p>
          ) : canManage ? (
            <div className="flex flex-col gap-1.5">
              <Label className="text-[11px] font-semibold uppercase tracking-[0.6px] text-muted-foreground">
                {dict.templateLabel}
              </Label>
              <TeamTemplateCombobox
                templates={allTemplates}
                selectedTemplateId={assignedTemplateId}
                triggerLabel={triggerLabel}
                triggerClassName={TEMPLATE_TRIGGER_CLASS}
                placeholder={dict.templatePlaceholder}
                searchPlaceholder={dict.templateSearch}
                emptyMessage={dict.templateEmpty}
                noneLabel={dict.templateNone}
                locale={locale}
                onSelect={persistAssignment}
              />
            </div>
          ) : assignedTemplate ? (
            <p className="text-[13.5px] font-medium leading-relaxed text-foreground">
              {assignedTemplate.name}
            </p>
          ) : null}
        </SettingsGroupRow>

        {!loading && allTemplates.length > 0 && assignedTemplate ? (
          <SettingsGroupRow separated>
            <div className="flex flex-col gap-3">
              {assignedTemplate.description ? (
                <p className="text-[13.5px] leading-relaxed text-muted-foreground">
                  {assignedTemplate.description}
                </p>
              ) : null}
              {canManage ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-full justify-start gap-2"
                  onClick={() =>
                    navigateShell(
                      shellPaths.moreProfileTemplateDetail(assignedTemplate.id),
                    )
                  }
                >
                  <ExternalLink className="h-4 w-4 shrink-0" />
                  {dict.openTemplate}
                </Button>
              ) : null}
            </div>
          </SettingsGroupRow>
        ) : null}
      </SettingsGroupRows>
    </SettingsSection>
  );
}
